import L from 'leaflet';
import { store } from '../../../store';
import { setSelectedFeature } from '../../../store/slices/selectedFeatureSlice';
import { fetchLoteDataDashboard } from '../../../store/slices/loteDataDashboardSlice';
import { getStyleLayer, createStyleTooltip } from './layerStyles';
import { normalizeStatus } from '../../../config/layerDictionary.js';
import { addPendingReviewPin } from './pendingReviewPin';
import {
	keyNamePopupDatabase,
	layerDatabaseComCustomPopup,
	camposPopupLayersLinhas,
	camposPopupClassificacao,
} from '../config/dictionaryPopup';

const normalizeBe = value =>
	String(value ?? '')
		.trim()
		.toUpperCase();

export function createLayer(
	map,
	paneId,
	key,
	layerName,
	statusFilter,
	tooltips,
	isLineGeometry,
	onPopupOpen,
	beFilter = [],
	onPendingReviewOpen
) {
	const selectedBes = new Set(
		(Array.isArray(beFilter) ? beFilter : [])
			.map(normalizeBe)
			.filter(Boolean)
	);

	const layer = L.geoJSON([], {
		pane: paneId,
		render: L.canvas(),
		chunkedLoading: true,

		style: ((layerNameCopy, isLineCopy) => feature => {
			return getStyleLayer(feature, layerNameCopy, isLineCopy);
		})(layerName, isLineGeometry),
		filter: feature => {
			// O filtro por BE tem prioridade e ignora completamente o status.
			if (selectedBes.size > 0) {
				const featureBe = normalizeBe(feature.properties?.be);
				return selectedBes.has(featureBe);
			}

			if (!statusFilter) return true;
			if (layerName === 'Blocos de Entrega') {
				const lote = parseInt(statusFilter.split(' ')[1]);
				return feature.properties?.lote === lote;
			}
			const featureStatus = normalizeStatus(feature.properties?.status);
			const filterStatus = normalizeStatus(statusFilter);

			return featureStatus === filterStatus;
		},
		onEachFeature: (feature, featureLayer) => {
			// Etapa 1 da revisão de produto: exibe PIN vermelho somente em
			// polígonos com status 'Aprovado com Pendência' e apenas para
			// empresas diferentes de SIMEPAR/IAT.
			addPendingReviewPin(
				feature,
				layer,
				layerName,
				onPendingReviewOpen
			);

			if (feature.properties) {
				let popupContent = '';

				// LINHAS
				if (layerDatabaseComCustomPopup.includes(layerName)) {
					// Apenas aplica se for linha, um contorno em volta para clicar melhor
					if (isLineGeometry) {
						const clickableLayer = L.geoJSON(feature, {
							pane: paneId,
							style: {
								color: '#000000',
								weight: 15,
								opacity: 0,
								interactive: true,
							},
						});

						clickableLayer.on('click', e => {
							featureLayer.openPopup(e.latlng);
							if (onPopupOpen) onPopupOpen();
						});

						clickableLayer.addTo(map);
						featureLayer._clickableLayer = clickableLayer;
						if (!layer._clickableLayers)
							layer._clickableLayers = [];
						layer._clickableLayers.push(clickableLayer);
					}

					const propriedades = feature.properties || {};
					const popupCampos = [];
					const jaAdicionados = new Set();

					let camposOrdem;
					if (layerName === 'Classificação da Nuvem De Pontos') {
						camposOrdem = camposPopupClassificacao;
					} else {
						camposOrdem = camposPopupLayersLinhas;
					}

					camposOrdem.forEach(k => {
						const valor = propriedades[k];
						const label = keyNamePopupDatabase[k] || k;
						if (valor !== undefined && !jaAdicionados.has(label)) {
							popupCampos.push(
								`<strong>${label}:</strong> ${valor}`
							);
							jaAdicionados.add(label);
						}
					});

					popupContent = popupCampos.join('<br/>');
				} else {
					popupContent = Object.keys(feature.properties)
						.filter(
							k =>
								![
									'ogc_fid',
									'usuario_upload',
									'fid',
									'data_upload',
									'fuso',
								].includes(k)
						)
						.map(
							k =>
								`<strong>${keyNamePopupDatabase[k] || k}:</strong> ${feature.properties[k]}`
						)
						.join('<br/>');
				}
				featureLayer.bindPopup(popupContent);
				featureLayer.on('popupopen', () => {
					const lote = feature.properties?.lote ?? null;

					store.dispatch(
						setSelectedFeature({
							lote: lote,
							tabela: layerName,
							properties: feature.properties,
						})
					);

					if (lote !== null && layerName) {
						store.dispatch(
							fetchLoteDataDashboard({
								nomeTabela: layerName,
								lote: lote,
							})
						);
					}

					if (onPopupOpen) onPopupOpen();
				});
			}

			if (layerName === 'Blocos de Entrega') {
				const loteSelecionado = statusFilter
					? statusFilter.split(' ')[1]
					: null;
				const tooltip = createStyleTooltip(
					feature,
					layerName,
					featureLayer,
					loteSelecionado
				);
				if (tooltip) {
					tooltip.addTo(map);
					tooltips.push(tooltip);
				}
			}
		},
	}).addTo(map);

	layer._tooltips = tooltips;
	layer.options.layerName = layerName;
	layer.options.statusFilter = statusFilter;
	layer.options.beFilter = beFilter;

	return layer;
}
